import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ClipboardList, Lock } from "lucide-react";
import { StudentSurveyDialog } from "./StudentSurveyDialog";
import { dismissStudentSurvey, fetchStudentSurveyStatus, fetchSurvey, submitSurvey } from "../../lib/surveys";

/**
 * Moment two: once a grant has landed (or a final installment is waiting on
 * feedback) the post-grant survey pops up on the next dashboard visit. Any
 * close is "ask me later" for 7 days; this card stays as the quiet fallback.
 */
export const StudentSurveyCard = () => {
  const [status, setStatus] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [open, setOpen] = useState(false);
  const [thanks, setThanks] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchStudentSurveyStatus(), fetchSurvey("student_grant")])
      .then(([st, sv]) => {
        if (cancelled) return;
        setStatus(st.grant);
        setSurvey(sv);
        const g = st.grant;
        const snoozed = g.dismissed_until && new Date(g.dismissed_until) > new Date();
        if (g.enabled && !g.completed && (g.grant_landed || g.hold) && !snoozed && sv.questions?.length) {
          setTimeout(() => !cancelled && setOpen(true), 800);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!status || !survey || !status.enabled || !survey.questions?.length) return null;
  if (status.completed && !thanks) return null;
  if (!status.grant_landed && !status.hold) return null;
  const snoozed = status.dismissed_until && new Date(status.dismissed_until) > new Date();

  const later = async (done) => {
    setOpen(false);
    if (done) return;
    const r = await dismissStudentSurvey().catch(() => null);
    if (r) setStatus((s) => ({ ...s, dismissed_until: r.dismissed_until }));
  };

  const onSubmit = async (answers) => {
    const r = await submitSurvey("student_grant", { touchpoint: status.grant_landed ? "post_grant" : "final_hold", answers });
    setThanks(r.thanks);
    setStatus((s) => ({ ...s, completed: true }));
    return r.thanks;
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="student-survey-card"
      className="rounded-2xl border-2 border-yellow bg-yellow/10 p-6 lg:p-8"
    >
      {thanks ? (
        <div className="flex items-start gap-4" data-testid="student-survey-thanks">
          <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-grass" strokeWidth={2} />
          <div>
            <h3 className="font-serif text-2xl text-ink">Thank you.</h3>
            <p className="mt-1 text-base text-olive">{thanks}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-yellow">
              <ClipboardList className="h-5 w-5" strokeWidth={2} />
            </span>
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                {status.hold && !status.grant_landed ? "A grant is waiting on your feedback" : "Beta feedback"}
                {status.required_before_final && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-ink px-2 py-0.5 text-[10px] text-sand"><Lock className="h-3 w-3" /> Required before your final installment</span>}
              </p>
              <h3 className="mt-2 font-serif text-2xl leading-tight text-ink lg:text-3xl">{survey.title}</h3>
              <p className="mt-2 max-w-2xl text-base leading-relaxed text-olive">{survey.intro}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={() => setOpen(true)} data-testid="student-survey-open" className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand hover:bg-forest">
              Start the survey
            </button>
            {!snoozed && (
              <button type="button" onClick={() => later(false)} data-testid="student-survey-later" className="text-sm font-semibold text-olive underline-offset-4 hover:text-ink hover:underline">
                Ask me later
              </button>
            )}
          </div>
        </div>
      )}
      <StudentSurveyDialog
        survey={survey}
        open={open}
        testid="grant-survey"
        eyebrow={status.required_before_final ? "Beta feedback, required before your final installment" : "Beta feedback, two minutes"}
        skipLabel="Ask me later"
        submitLabel="Submit my feedback"
        onSkip={later}
        onSubmit={onSubmit}
      />
    </motion.section>
  );
};
